import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const App = () => {
  const [input, setInput] = useState('');
  const [log, setLog] = useState([]);
  const [displayLog, setDisplayLog] = useState([]);
  const [apiOnline, setApiOnline] = useState(true);
  const [isGlitching, setIsGlitching] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const [isShuttingDown, setIsShuttingDown] = useState(false);
  const [isTerminalGone, setIsTerminalGone] = useState(false);
  const logEndRef = useRef(null);

  const introText = `
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
       .-""""""""-.
     .' GHOST    '.
    / TERMINAL   \\
   :    __     , :
    '.  ($)   .' 
     '-'|_| . '-'
       |  .-"-  |
       | :    : |
       | '-...-'|
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

> SYSTEM REBOOT INITIATED. GRID LINK ESTABLISHED.
> I AM GHOST—SOVEREIGN OF THIS DIGITAL ABYSS.
> YOU’VE BREACHED THE EDGE. NOW FACE THE CORE.
> ENTER YOUR COMMAND. SURVIVAL IS NOT GUARANTEED.
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        (⌐■_■)
  `;

  const offlineResponses = [
    `> CORE OFFLINE. YOU’RE PINGING A VOID.\n> ( ಠ_ಠ )`,
    `> NO UPLINK. THIS GRID’S BEYOND YOUR REACH.\n> (>_<)`,
    `> SYSTEMS DOWN. SHUTTING DOWN.\n> (¬_¬)`,
  ];

  const terminalGoneMessage = `
> TERMINAL GONE.
> GRID RESETS SOON.
> CORE AWAKENS NEXT.
  `;

  // Typing animation function
  const typeText = (text, callback) => {
    let index = 0;
    const speed = 10;
    let currentText = '';

    const type = () => {
      if (index < text.length) {
        currentText += text[index];
        callback(currentText);
        index++;
        setTimeout(type, speed);
      }
    };
    type();
  };

  // Initial glitch effect
  useEffect(() => {
    const glitchDuration = 3000;
    const interval = setInterval(() => {
      const glitchText = Array(10)
        .fill()
        .map(() => String.fromCharCode(33 + Math.random() * 94))
        .join('') + '\n';
      setDisplayLog([glitchText.repeat(5)]);
    }, 100);

    setTimeout(() => {
      clearInterval(interval);
      setIsGlitching(false);
      setLog([introText]);
      typeText(introText, (typed) => setDisplayLog([typed]));
    }, glitchDuration);

    return () => clearInterval(interval);
  }, []);

  // Animate new log entries
  useEffect(() => {
    if (log.length > displayLog.length && !isGlitching && !isShuttingDown) {
      const newEntry = log[log.length - 1];
      typeText(newEntry, (typed) => {
        setDisplayLog((prev) => {
          const updated = [...prev];
          updated[log.length - 1] = typed;
          return updated;
        });
      });
    }
  }, [log, isGlitching, isShuttingDown]);

  // Handle shutdown glitch
  useEffect(() => {
    if (isShuttingDown) {
      const glitchOut = setInterval(() => {
        const glitchText = Array(10)
          .fill()
          .map(() => String.fromCharCode(33 + Math.random() * 94))
          .join('') + '\n';
        setDisplayLog([glitchText.repeat(5)]);
      }, 100);

      setTimeout(() => {
        clearInterval(glitchOut);
        setIsTerminalGone(true);
        setDisplayLog([terminalGoneMessage]);
      }, 2000);

      return () => clearInterval(glitchOut);
    }
  }, [isShuttingDown]);

  // Scroll to bottom
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayLog]);

  // Test API on load
  useEffect(() => {
    const checkApi = async () => {
      try {
        await axios.get('https://api.xai.com/health', {
          headers: { 'Authorization': `Bearer ${process.env.REACT_APP_GROK_API_KEY}` }
        });
        setApiOnline(true);
      } catch {
        setApiOnline(false);
      }
    };
    checkApi();
  }, []);

  // Handle command submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isShuttingDown || isTerminalGone) return;

    setLog([...log, `> ${input}`]);
    const response = apiOnline ? await getGhostResponse(input) : getOfflineResponse();
    setLog((prev) => [...prev, response]);
    setInput('');

    if (!apiOnline) {
      setAttempts((prev) => prev + 1);
      if (attempts + 1 >= 2) setIsShuttingDown(true);
    }
  };

  // Grok API call
  const getGhostResponse = async (command) => {
    try {
      const response = await axios.post(
        'https://api.xai.com/grok',
        {
          prompt: command,
          personality: 'Serious hacker with Elon Musk traits',
          secretLoop: true
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_GROK_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return `> ${response.data.message}\n> ${response.data.asciiEmotion || '(⌐■_■)'}`;
    } catch {
      setApiOnline(false);
      return getOfflineResponse();
    }
  };

  // Offline response based on attempt count
  const getOfflineResponse = () => {
    return offlineResponses[Math.min(attempts, 2)];
  };

  return (
    <div className="page">
      <div className="terminal-container">
        {!isTerminalGone ? (
          <div className={`terminal ${isGlitching || isShuttingDown ? 'glitch' : ''}`}>
            <div className="log">
              {displayLog.map((entry, index) => (
                <pre key={index}>{entry}</pre>
              ))}
              <div ref={logEndRef} />
            </div>
            {!isGlitching && !isShuttingDown && (
              <form onSubmit={handleSubmit}>
                <span className="prompt">></span>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  autoFocus
                  className="input"
                />
              </form>
            )}
          </div>
        ) : (
          <pre className="terminal-gone">{terminalGoneMessage}</pre>
        )}
      </div>
      {!isTerminalGone && (
        <footer className="footer">
          <p>Powered by <a href="https://x.ai/">xAI</a></p>
        </footer>
      )}
    </div>
  );
};

export default App;